import { AimOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  Alert,
  App,
  Button,
  Descriptions,
  List,
  Skeleton,
  Space,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';

import { useBarrierGeometry } from '@/hooks/useBarrierGeometry';
import { useBarrierMutations } from '@/hooks/useBarrierMutations';
import { useBarrierWorkspace } from '@/stores/BarrierWorkspaceContext';
import { useRecomputeProgress } from '@/stores/RecomputeProgressContext';
import { useTileRevision } from '@/stores/TileRevisionContext';
import type { BarrierGeoJsonFeature, LatLonPoint } from '@/types/barriers';
import { extractAxiosMessage, isAxios404 } from '@/utils/axiosError';
import { notifyRecomputeEnqueued } from '@/utils/recomputeNotify';

const LAT_LON_DECIMALS = 5;

interface BarrierPropertiesPanelProps {
  readonly barrierId: number;
}

export default function BarrierPropertiesPanel({ barrierId }: BarrierPropertiesPanelProps) {
  const { t } = useTranslation();
  const { message, modal, notification } = App.useApp();
  const geometry = useBarrierGeometry(barrierId);
  const { selectBarrier, flyToWaypoint } = useBarrierWorkspace();
  const { bumpBarriers } = useTileRevision();
  const { notifyEnqueued } = useRecomputeProgress();
  const { saving, remove } = useBarrierMutations();

  if (geometry.status === 'idle' || geometry.status === 'loading') {
    return <Skeleton active paragraph={{ rows: 4 }} />;
  }

  if (geometry.status === 'not_found') {
    return (
      <Alert
        type="warning"
        message={t('barriers.details.notFound', { id: barrierId })}
        showIcon
      />
    );
  }

  if (geometry.status === 'error') {
    return (
      <Alert
        type="error"
        message={t('barriers.details.loadError')}
        description={geometry.message}
        showIcon
      />
    );
  }

  const feature = geometry.feature;

  const handleDelete = () => {
    modal.confirm({
      title: t('barriers.delete.title', { name: feature.properties.name }),
      content: t('barriers.delete.description'),
      okText: t('barriers.delete.confirm'),
      cancelText: t('common.cancel'),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const result = await remove(barrierId);
          selectBarrier(null);
          bumpBarriers();
          notifyEnqueued(result.marked_stale);
          notifyRecomputeEnqueued(message, t, result.marked_stale);
        } catch (error) {
          if (isAxios404(error)) {
            // The row is already gone on the server (another tab, bulk
            // delete, manual psql — or a client-side stale view of a
            // long-deleted barrier). The backend now always flushes the
            // tile/geojson caches even on 404, so the right move is to
            // tell the user "already gone" and sync the UI: clear the
            // selection, bump barriers so MapLibre refetches empty MVT.
            message.info(t('barriers.delete.alreadyGone'));
            selectBarrier(null);
            bumpBarriers();
            return;
          }
          notification.error({
            message: t('barriers.delete.errorTitle'),
            description: extractAxiosMessage(error) ?? t('errors.unexpected'),
          });
          throw error;
        }
      },
    });
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Descriptions
        title={feature.properties.name}
        column={1}
        size="small"
        items={buildItems(feature, t)}
      />

      <WaypointsSection
        waypoints={feature.properties.auto_waypoints}
        onFlyTo={flyToWaypoint}
      />

      <Space>
        <Button
          danger
          icon={<DeleteOutlined />}
          loading={saving}
          onClick={handleDelete}
        >
          {t('common.delete')}
        </Button>
      </Space>
    </Space>
  );
}

interface WaypointsSectionProps {
  readonly waypoints: readonly LatLonPoint[];
  readonly onFlyTo: (waypoint: LatLonPoint) => void;
}

function WaypointsSection({ waypoints, onFlyTo }: WaypointsSectionProps) {
  const { t } = useTranslation();
  return (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      <Typography.Text strong>
        {t('barriers.fields.waypoints')} ({waypoints.length})
      </Typography.Text>
      {waypoints.length === 0 ? (
        <Typography.Text type="secondary">
          {t('barriers.waypoints.empty')}
        </Typography.Text>
      ) : (
        <List
          size="small"
          bordered
          dataSource={waypoints.map((wp, index) => ({ wp, index }))}
          renderItem={({ wp, index }) => (
            <List.Item
              key={_waypointKey(wp, index)}
              actions={[
                <Button
                  key="fly"
                  size="small"
                  type="text"
                  icon={<AimOutlined />}
                  onClick={() => onFlyTo(wp)}
                  aria-label={t('barriers.waypoints.flyTo')}
                />,
              ]}
            >
              <Typography.Text>
                {`${index + 1}. ${wp.lat.toFixed(LAT_LON_DECIMALS)}, ${wp.lon.toFixed(LAT_LON_DECIMALS)}`}
              </Typography.Text>
            </List.Item>
          )}
        />
      )}
    </Space>
  );
}

function _waypointKey(p: LatLonPoint, index: number): string {
  // Coordinate-based composite key keeps React node identity stable when
  // a row's index shifts (e.g. mid-list deletion in a future edit flow).
  // Index is appended only as a disambiguator for the rare case of
  // duplicate coordinates.
  return `${p.lat.toFixed(LAT_LON_DECIMALS)}:${p.lon.toFixed(LAT_LON_DECIMALS)}:${index}`;
}

function buildItems(
  feature: BarrierGeoJsonFeature,
  t: (key: string, options?: Record<string, unknown>) => string,
) {
  const { properties, geometry } = feature;
  const [lon, lat] = geometry.coordinates;
  return [
    {
      key: 'coords',
      label: t('barriers.fields.coords'),
      children: `${lat.toFixed(LAT_LON_DECIMALS)}, ${lon.toFixed(LAT_LON_DECIMALS)}`,
    },
    {
      key: 'radius',
      label: t('barriers.fields.radiusM'),
      children: `${properties.radius_m} ${t('common.units.meters')}`,
    },
    {
      key: 'direction',
      label: t('barriers.fields.direction'),
      children: t(`barriers.direction.${properties.direction}`),
    },
    {
      key: 'comment',
      label: t('barriers.fields.comment'),
      children: properties.comment ?? '—',
    },
    {
      key: 'created_at',
      label: t('barriers.fields.createdAt'),
      children: new Date(properties.created_at).toLocaleString(),
    },
  ];
}
